import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Minus, Plus, ArrowLeft, Truck, Shield, RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { StoreHeader } from '../components/StoreHeader';
import { StoreFooter } from '../components/StoreFooter';
import { useCartStore } from '../stores/cartStore';
import { productApi, flashSaleApi } from '../lib/api';
import { toast } from 'sonner';
import { AddToCartModal } from "../components/AddToCartModal";
import { Helmet } from 'react-helmet-async';

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [flashSale, setFlashSale] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');

  const addItem = useCartStore((state) => state.addItem);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const [productRes, flashSalesRes] = await Promise.all([
          productApi.getById(id),
          flashSaleApi.getAll(true),
        ]);
        setProduct(productRes.data);
        const firstSize = productRes.data.sizes?.[0] || '';
        const firstColor = productRes.data.colors?.[0] || '';
        setSelectedSize(firstSize);
        setSelectedColor(firstColor);

        const sale = flashSalesRes.data.find((f) => f.product_id === id);
        setFlashSale(sale);
      } catch (error) {
        console.error('Error loading product:', error);
        toast.error('Product not found');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, navigate]);

  const [showModal, setShowModal] = useState(false);

  const handleAddToCart = () => {
    if (!product) return;

    const price = flashSale
      ? product.price * (1 - flashSale.discount_percent / 100)
      : product.price;

    for (let i = 0; i < quantity; i++) {
      addItem({
        id: product.id,
        name: product.name,
        price,
        original_price: product.original_price,
        image: product.images?.[0] || '',
        size: selectedSize,
        color: selectedColor,
      });
    }

    toast.success(`${quantity}x ${product.name} added to cart!`);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100">
        <StoreHeader />
        <div className="max-w-7xl mx-auto px-4 py-16 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!product) return null;

  const displayPrice = flashSale
    ? product.price * (1 - flashSale.discount_percent / 100)
    : product.price;

  const hasMultipleImages = product.images?.length > 1;

  return (
    <div className="min-h-screen bg-stone-100">
      <StoreHeader />

      <Helmet>
        <title>{product.name} | Megamart</title>
        <meta name="description" content={product.description} />

        {/* Open Graph */}
        <meta property="og:title" content={product.name} />
        <meta property="og:description" content={product.description} />
        <meta property="og:image" content={product.images[0]} />
        <meta property="og:url" content={`https://megamart.co.ke/products/${product.id}`} />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="Megamart" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={product.name} />
        <meta name="twitter:description" content={product.description} />
        <meta name="twitter:image" content={product.images[0]} />
      </Helmet>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-stone-500 mb-6 flex items-center gap-2">
          <Link to="/products" className="hover:text-emerald-900 flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back to Products
          </Link>
        </nav>

        <div className="bg-white border border-stone-200 p-4 sm:p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* Images */}
            <div>
              <div className="aspect-square bg-stone-100 p-4 sm:p-6 md:p-8 overflow-hidden">
                <img
                  src={product.images?.[selectedImage] || 'https://via.placeholder.com/600'}
                  alt={product.name}
                  className="w-full h-full object-contain"
                  data-testid="product-main-image"
                />
              </div>

              {hasMultipleImages && (
                <div className="flex gap-2 overflow-x-auto mt-4">
                  {product.images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImage(i)}
                      className={`w-20 h-20 flex-shrink-0 border-2 ${selectedImage === i ? 'border-emerald-900' : 'border-stone-200'
                        }`}
                    >
                      <img
                        src={img}
                        alt={`Thumbnail ${i + 1}`}
                        className="w-full h-full object-contain"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div>
              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                {flashSale && (
                  <Badge className="bg-red-600 text-white">
                    {flashSale.discount_percent}% OFF
                  </Badge>
                )}
                {product.featured && <Badge variant="outline">Featured</Badge>}
                <Badge variant="secondary">{product.category_name}</Badge>
              </div>

              {/* Title */}
              <h1
                className="text-2xl sm:text-3xl font-bold text-stone-900 mb-4"
                data-testid="product-title"
              >
                {product.name}
              </h1>

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-6">
                <span
                  className="text-2xl sm:text-4xl font-bold text-emerald-900"
                  data-testid="product-price"
                >
                  KES {displayPrice.toLocaleString()}
                </span>
                {(flashSale || product.original_price) && (
                  <span className="text-lg sm:text-xl text-stone-400 line-through">
                    KES {product.original_price.toLocaleString()}
                  </span>
                )}
              </div>

              {/* Description */}
              <p className="text-stone-600 mb-6 leading-relaxed whitespace-pre-line break-words">
                {product.description}
              </p>

              {/* Stock Status */}
              <div className="mb-6">
                {product.stock > 10 ? (
                  <span className="text-green-600 font-medium">&#10003; In Stock</span>
                ) : product.stock > 0 ? (
                  <span className="text-orange-600 font-medium">
                    Only {product.stock} left!
                  </span>
                ) : (
                  <span className="text-red-600 font-medium">Out of Stock</span>
                )}
              </div>

              {/* Quantity */}
              <div className="flex items-center gap-4 mb-6">
                <span className="font-medium">Quantity:</span>
                <div className="flex items-center border border-stone-200">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    data-testid="quantity-decrease"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span
                    className="w-12 text-center font-medium"
                    data-testid="quantity-value"
                  >
                    {quantity}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setQuantity(Math.min(product.stock, quantity + 1))
                    }
                    disabled={quantity >= product.stock}
                    data-testid="quantity-increase"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Sizes */}
              {product.sizes?.length > 0 && (
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-medium">Available Sizes:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {product.sizes.map((size, i) => (
                      <Badge
                        key={i}
                        variant={selectedSize === size ? "default" : "outline"}
                        className={`cursor-pointer ${selectedSize === size ? "bg-emerald-900 text-white" : ""}`}
                        onClick={() => setSelectedSize(size)}
                      >
                        {size}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Colors */}
              {product.colors?.length > 0 && (
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-medium">Available Colors:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {product.colors.map((color, i) => (
                      <Badge
                        key={i}
                        variant={selectedColor === color ? "default" : "secondary"}
                        className={`cursor-pointer ${selectedColor === color ? "bg-emerald-900 text-white" : ""}`}
                        onClick={() => setSelectedColor(color)}
                      >
                        {color}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Add to Cart */}
              <Button
                size="lg"
                className="w-full bg-emerald-900 hover:bg-emerald-800 uppercase tracking-wider font-bold py-4 sm:py-6 text-base sm:text-lg btn-shadow"
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Add to Cart – KES {(displayPrice * quantity).toLocaleString()}
              </Button>

              {/* Features */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8 pt-8 border-t border-stone-200">
                <div className="text-center">
                  <Truck className="h-6 w-6 mx-auto text-emerald-600 mb-2" />
                  <p className="text-xs text-stone-600">Free Delivery</p>
                </div>
                <div className="text-center">
                  <Shield className="h-6 w-6 mx-auto text-emerald-600 mb-2" />
                  <p className="text-xs text-stone-600">Secure Payment</p>
                </div>
                <div className="text-center">
                  <RefreshCw className="h-6 w-6 mx-auto text-emerald-600 mb-2" />
                  <p className="text-xs text-stone-600">Easy Returns</p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </main>

      <StoreFooter />

      <AddToCartModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        product={product}
      />

    </div>
  );
}
